import React, { useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { isMapAlive } from './mapGuard';
import type { FeatureCollection } from 'geojson';
import type { LayerId, LayerVisibility } from '../../../types/mapEngine';

interface Props {
  map: mapboxgl.Map;
  topology: {
    taxiways: FeatureCollection;
    stands: FeatureCollection;
    aipStands?: FeatureCollection | null;
    importedTopology?: FeatureCollection | null;
  } | null;
  layerVisibility: LayerVisibility;
}

const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] };

const VISIBILITY_MAP: Record<LayerId, string[]> = {
  'base-runways':      ['me-runways-fill', 'me-runways-surface', 'me-runways-centerline', 'me-runways-outline', 'me-runway-labels'],
  'base-aprons':       ['me-aprons-fill', 'me-aprons-outline'],
  'base-taxiways':     ['me-taxiways-line', 'me-taxiways-border'],
  'base-twy-labels':   ['me-twy-labels'],
  'base-stands':       ['me-stands-circle'],
  'base-stand-labels': ['me-stand-labels'],
  'base-agl-spots':    ['me-agl-spots'],
  'heatmap-congestion':          [],
  'heatmap-arrivals':            [],
  'heatmap-departures':          [],
  'heatmap-taxi-risk':           [],
  'heatmap-taxi-speed':          [],
  'heatmap-ci-graph':            [],
  'heatmap-tcf-graph':           [],
  'heatmap-mht-graph':           [],
  'heatmap-pushback-risk':       [],
  'conops-aor':                  [],
  'conops-taxi-routes':          [],
  'conops-pushback-profiles':    [],
  'conops-aptr':                 [],
  'conops-roundabout':           [],
  'conops-handover-points':      [],
  'aviplan-swept-paths':         [],
  'aviplan-safety-envelopes':    [],
  'aviplan-jet-blast':           [],
  'aircraft-silhouettes':        [],
};

const BaseAirportLayer: React.FC<Props> = ({ map, topology, layerVisibility }) => {

  // Effect 1: create sources and layers once on mount
  useEffect(() => {
    // importedTopology (AIP/CAD extraction with GCP georeferencing) takes priority
    // over the OSM base when present. Falls back to OSM taxiways otherwise.
    const taxiwayData: FeatureCollection = topology?.importedTopology ?? topology?.taxiways ?? EMPTY_FC;
    const standData: FeatureCollection   = topology?.aipStands ?? topology?.stands ?? EMPTY_FC;

    if (!map.getSource('me-taxiways')) {
      map.addSource('me-taxiways', { type: 'geojson', data: taxiwayData });
    }
    if (!map.getSource('me-stands')) {
      map.addSource('me-stands', { type: 'geojson', data: standData });
    }

    // ── Apron fill (Polygon) ──────────────────────────────────────────────
    if (!map.getLayer('me-aprons-fill')) {
      map.addLayer({
        id: 'me-aprons-fill',
        type: 'fill',
        source: 'me-taxiways',
        filter: ['==', ['get', 'aeroway'], 'apron'],
        paint: {
          'fill-color': '#3a2510',
          'fill-opacity': 0.92,
        },
      });
    }

    if (!map.getLayer('me-aprons-outline')) {
      map.addLayer({
        id: 'me-aprons-outline',
        type: 'line',
        source: 'me-taxiways',
        filter: ['==', ['get', 'aeroway'], 'apron'],
        paint: {
          'line-color': '#6b4f22',
          'line-width': 1,
        },
      });
    }

    // ── Runway fill — covers Polygon runways from CAD/AIP imports ────────
    if (!map.getLayer('me-runways-fill')) {
      map.addLayer({
        id: 'me-runways-fill',
        type: 'fill',
        source: 'me-taxiways',
        filter: ['all', ['==', ['get', 'aeroway'], 'runway'], ['==', ['geometry-type'], 'Polygon']],
        paint: {
          'fill-color': '#62626e',
          'fill-opacity': 1,
        },
      });
    }

    // ── Runway surface — thick line representing the 45 m pavement ────────
    // OSM runways are stored as centerline LineStrings.
    // At MPTO lat ~9°N, zoom 14 ≈ 9.4 m/px → 45m runway ≈ 5px at zoom 14.
    if (!map.getLayer('me-runways-surface')) {
      map.addLayer({
        id: 'me-runways-surface',
        type: 'line',
        source: 'me-taxiways',
        filter: ['==', ['get', 'aeroway'], 'runway'],
        paint: {
          'line-color': '#62626e',
          'line-width': [
            'interpolate', ['exponential', 2], ['zoom'],
            12, 2,
            14, 6,
            16, 22,
            18, 88,
          ],
          'line-opacity': 1,
        },
      });
    }

    // ── Runway edge lines (white borders left + right of centerline) ──────
    if (!map.getLayer('me-runways-outline')) {
      map.addLayer({
        id: 'me-runways-outline',
        type: 'line',
        source: 'me-taxiways',
        filter: ['==', ['get', 'aeroway'], 'runway'],
        paint: {
          'line-color': '#c8c8c8',
          'line-width': 1.5,
          'line-gap-width': [
            'interpolate', ['exponential', 2], ['zoom'],
            12, 2,
            14, 6,
            16, 22,
            18, 88,
          ],
          'line-opacity': 0.8,
        },
      });
    }

    // ── Runway centerline dashes ──────────────────────────────────────────
    if (!map.getLayer('me-runways-centerline')) {
      map.addLayer({
        id: 'me-runways-centerline',
        type: 'line',
        source: 'me-taxiways',
        filter: ['==', ['get', 'aeroway'], 'runway'],
        minzoom: 13.5,
        paint: {
          'line-color': '#ffffff',
          'line-width': 1.5,
          'line-opacity': 0.5,
          'line-dasharray': [10, 10],
        },
      });
    }

    // ── Taxiway border (dark border below yellow centerline) ──────────────
    if (!map.getLayer('me-taxiways-border')) {
      map.addLayer({
        id: 'me-taxiways-border',
        type: 'line',
        source: 'me-taxiways',
        filter: ['==', ['get', 'aeroway'], 'taxiway'],
        paint: {
          'line-color': '#1a1a28',
          'line-width': [
            'interpolate', ['linear'], ['zoom'],
            12, 3,
            14, 7,
            16, 16,
          ],
        },
      });
    }

    // ── Taxiway centerline (amber/yellow) ─────────────────────────────────
    if (!map.getLayer('me-taxiways-line')) {
      map.addLayer({
        id: 'me-taxiways-line',
        type: 'line',
        source: 'me-taxiways',
        filter: ['==', ['get', 'aeroway'], 'taxiway'],
        paint: {
          'line-color': '#c8a000',
          'line-width': [
            'interpolate', ['linear'], ['zoom'],
            12, 1.5,
            14, 3.5,
            16, 8,
          ],
          'line-opacity': 0.95,
        },
      });
    }

    // ── Runway designator labels ──────────────────────────────────────────
    if (!map.getLayer('me-runway-labels')) {
      map.addLayer({
        id: 'me-runway-labels',
        type: 'symbol',
        source: 'me-taxiways',
        filter: ['all',
          ['==', ['get', 'aeroway'], 'runway'],
          ['has', 'ref'],
        ],
        minzoom: 12,
        layout: {
          'text-field': ['get', 'ref'],
          'text-size': 12,
          'symbol-placement': 'line-center',
          'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
          'text-rotation-alignment': 'map',
        },
        paint: {
          'text-color': '#e8e8f0',
          'text-halo-color': '#000',
          'text-halo-width': 1.5,
        },
      });
    }

    // ── Taxiway labels ────────────────────────────────────────────────────
    if (!map.getLayer('me-twy-labels')) {
      map.addLayer({
        id: 'me-twy-labels',
        type: 'symbol',
        source: 'me-taxiways',
        filter: ['all',
          ['==', ['get', 'aeroway'], 'taxiway'],
          ['has', 'ref'],
        ],
        minzoom: 13,
        layout: {
          'text-field': ['get', 'ref'],
          'text-size': 11,
          'symbol-placement': 'line-center',
          'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
        },
        paint: {
          'text-color': '#FFD700',
          'text-halo-color': '#000',
          'text-halo-width': 1.5,
        },
      });
    }

    // ── Stand circles ─────────────────────────────────────────────────────
    if (!map.getLayer('me-stands-circle')) {
      map.addLayer({
        id: 'me-stands-circle',
        type: 'circle',
        source: 'me-stands',
        paint: {
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            12, 3,
            14, 5,
            16, 8,
          ],
          'circle-color': [
            'match', ['get', 'icao_cat'],
            'E', '#EF5350',
            'D', '#FF7043',
            'C', '#FFA726',
            'B', '#FFEE58',
            '#90A4AE',
          ],
          'circle-stroke-color': 'rgba(255,255,255,0.7)',
          'circle-stroke-width': 1,
        },
      });
    }

    // ── Stand labels ──────────────────────────────────────────────────────
    if (!map.getLayer('me-stand-labels')) {
      map.addLayer({
        id: 'me-stand-labels',
        type: 'symbol',
        source: 'me-stands',
        minzoom: 13,
        layout: {
          'text-field': ['coalesce', ['get', 'stand_number'], ['get', 'ref'], ''],
          'text-size': 10,
          'text-offset': [0, 1.2],
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#000',
          'text-halo-width': 1.5,
        },
      });
    }

    // ── AGL / holding-position spots ─────────────────────────────────────
    if (!map.getLayer('me-agl-spots')) {
      map.addLayer({
        id: 'me-agl-spots',
        type: 'circle',
        source: 'me-stands',
        filter: ['==', ['get', 'aeroway'], 'holding_position'],
        paint: {
          'circle-radius': 5,
          'circle-color': '#43A047',
          'circle-stroke-color': 'rgba(255,255,255,0.5)',
          'circle-stroke-width': 0.75,
        },
      });
    }

    return () => {
      if (!isMapAlive(map)) return;
      [
        'me-agl-spots', 'me-stand-labels', 'me-stands-circle',
        'me-twy-labels', 'me-runway-labels',
        'me-taxiways-line', 'me-taxiways-border',
        'me-runways-centerline', 'me-runways-outline', 'me-runways-surface', 'me-runways-fill',
        'me-aprons-outline', 'me-aprons-fill',
      ].forEach((id) => { if (map.getLayer(id)) map.removeLayer(id); });
      if (map.getSource('me-stands'))   map.removeSource('me-stands');
      if (map.getSource('me-taxiways')) map.removeSource('me-taxiways');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Effect 2: push updated data into existing sources when topology arrives
  useEffect(() => {
    const taxiwayData: FeatureCollection = topology?.importedTopology ?? topology?.taxiways ?? EMPTY_FC;
    const standData: FeatureCollection   = topology?.aipStands ?? topology?.stands ?? EMPTY_FC;

    const twySource  = map.getSource('me-taxiways') as mapboxgl.GeoJSONSource | undefined;
    const stndSource = map.getSource('me-stands')   as mapboxgl.GeoJSONSource | undefined;
    if (twySource)  twySource.setData(taxiwayData);
    if (stndSource) stndSource.setData(standData);
  }, [map, topology]);

  // Effect 3: toggle layer visibility
  useEffect(() => {
    (Object.keys(VISIBILITY_MAP) as LayerId[]).forEach((key) => {
      const vis = layerVisibility[key];
      if (vis === undefined) return;
      VISIBILITY_MAP[key].forEach((layerId) => {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(layerId, 'visibility', vis ? 'visible' : 'none');
        }
      });
    });
  }, [map, layerVisibility]);

  return null;
};

export default BaseAirportLayer;
